//! Draws grid lines on the canvas. The grid lines fade as the user zooms out,
//! and disappears at higher zoom levels. We remove lines between cells that
//! overflow and merged cells. Grid lines also respect the sheet.clamp value.

import { events, type DirtyObject } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import {
  calculateAlphaForGridLines,
  getColumnVerticalRangesToDraw,
  getMergedCellExcludedColumnsForRow,
  getMergedCellExcludedRowsForColumn,
  getRowHorizontalRangesToDraw,
} from '@/app/gridGL/UI/gridUtils';
import type { Rect } from '@/app/quadratic-core-types';
import { colors } from '@/app/theme/colors';
import { sharedEvents } from '@/shared/sharedEvents';
import type { ILineStyleOptions } from 'pixi.js';
import { Graphics, Rectangle } from 'pixi.js';

interface GridLine {
  column?: number;
  row?: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

export class GridLines extends Graphics {
  currentLineStyle: ILineStyleOptions = { alpha: 0 };
  dirty = true;

  // cache of lines used for snapping
  gridLinesX: GridLine[] = [];
  gridLinesY: GridLine[] = [];

  constructor() {
    super();
    events.on('setDirty', this.setDirty);
    events.on('mergeCells', this.onMergeCellsChanged);
    events.on('gridSettings', this.onGridSettingsChange);
    sharedEvents.on('changeThemeAppearanceMode', this.onThemeChange);
  }

  destroy() {
    events.off('setDirty', this.setDirty);
    events.off('mergeCells', this.onMergeCellsChanged);
    events.off('gridSettings', this.onGridSettingsChange);
    sharedEvents.off('changeThemeAppearanceMode', this.onThemeChange);
    super.destroy();
  }

  private onThemeChange = () => {
    this.dirty = true;
    pixiApp.setViewportDirty();
  };

  private onGridSettingsChange = () => {
    this.dirty = true;
    pixiApp.setViewportDirty();
  };

  private setDirty = (dirty: DirtyObject) => {
    if (dirty.gridLines) {
      this.dirty = true;
    }
  };

  private onMergeCellsChanged = (sheetId: string) => {
    if (sheetId === sheets.current) {
      this.dirty = true;
    }
  };

  update = (bounds = pixiApp.viewport.getVisibleBounds(), scale = pixiApp.viewport.scale.x, forceRefresh = false) => {
    if (!this.dirty && !forceRefresh) {
      return;
    }
    this.dirty = false;
    this.clear();

    if (!gridSettings.showGridLines()) {
      this.visible = false;
      pixiApp.setViewportDirty();
      return;
    }

    const gridAlpha = calculateAlphaForGridLines(scale);
    if (gridAlpha === 0) {
      this.visible = false;
      this.currentLineStyle = { alpha: 0 };
      return;
    }

    this.visible = true;

    this.currentLineStyle = {
      width: 1,
      color: colors.gridLines,
      alpha: 0.2 * gridAlpha,
      alignment: 0.5,
      native: true,
    };
    this.lineStyle(this.currentLineStyle);
    this.gridLinesX = [];
    this.gridLinesY = [];

    const sheet = sheets.sheet;
    const offsets = sheet.offsets;
    const startCol = offsets.getColumnFromScreen(bounds.left);
    const endCol = offsets.getColumnFromScreen(bounds.right);
    const startRow = offsets.getRowFromScreen(bounds.top);
    const endRow = offsets.getRowFromScreen(bounds.bottom);
    const cellBounds = new Rectangle(startCol, startRow, endCol - startCol + 1, endRow - startRow + 1);
    const mergedRects = sheet.getMergeCellsInRect(cellBounds);

    const range = this.drawHorizontalLines(bounds, mergedRects, startCol, endCol);
    this.drawVerticalLines(bounds, range, mergedRects, startRow, endRow);
  };

  private drawVerticalLines(
    bounds: Rectangle,
    range: [number, number],
    mergedRects: Rect[],
    startRow: number,
    endRow: number
  ) {
    const sheet = sheets.sheet;
    const offsets = sheet.offsets;
    const columnPlacement = offsets.getXPlacement(bounds.left);
    const index = columnPlacement.index;
    const position = columnPlacement.position;
    const gridOverflowLines = sheets.sheet.gridOverflowLines;
    const contentClamp = sheet.getContentClamp();

    const top = bounds.top <= contentClamp.top ? contentClamp.top : bounds.top;
    const bottom = bounds.bottom >= contentClamp.bottom ? contentClamp.bottom : bounds.bottom;

    // draw 0-line if it's visible (since it's not part of the sheet anymore)
    if (bounds.left <= 0) {
      this.moveTo(0, top);
      this.lineTo(0, bottom);
    }

    // Track if we've drawn the rightmost content boundary line
    let drawnRightBoundary = false;

    let column = index;
    const offset = bounds.left - position;
    let size = 0;

    for (let x = bounds.left; x <= bounds.right + size - 1; x += size) {
      // don't draw grid lines when hidden or beyond content clamp
      // Use the actual drawing position (x - offset) for content clamp comparison
      const drawX = x - offset;
      if (size !== 0 && drawX >= contentClamp.left && drawX <= contentClamp.right) {
        // Check if this is the rightmost content boundary
        if (drawX === contentClamp.right) {
          drawnRightBoundary = true;
        }
        // Get overflow lines (excludes overflow areas)
        const overflowLines = gridOverflowLines.getColumnVerticalRange(column, range);

        // Precalculate excluded row ranges due to merged cells
        // Use the full visible row range, not just 'range' from horizontal lines
        const mergedExcludedRows = getMergedCellExcludedRowsForColumn(column, [startRow, endRow], mergedRects);

        // Get the full row range for this column
        const topRow = offsets.getRowFromScreen(top);
        const bottomRow = offsets.getRowFromScreen(bottom);
        const fullRowRange: [number, number] = [topRow, bottomRow];

        // Compute ranges to draw, combining overflow and merged cell exclusions
        // Follows the same pattern as GridOverflowLines
        const linesToDraw = getColumnVerticalRangesToDraw(column, fullRowRange, overflowLines, mergedExcludedRows);

        // Draw the calculated line segments
        if (linesToDraw && linesToDraw.length > 0) {
          for (const [y0, y1] of linesToDraw) {
            const start = offsets.getRowPlacement(y0).position;
            const end = Math.min(offsets.getRowPlacement(y1 + 1).position, bottom);
            this.moveTo(x - offset, start);
            this.lineTo(x - offset, end);
          }
        } else if (linesToDraw === undefined) {
          // No exclusions, draw the full line
          this.moveTo(x - offset, top);
          this.lineTo(x - offset, bottom);
        }
        // If linesToDraw is empty array, don't draw anything

        this.gridLinesX.push({ column, x: x - offset, y: top, w: 1, h: bottom - top });
      }
      size = sheets.sheet.offsets.getColumnWidth(column);
      column++;
    }

    // Draw the rightmost content boundary line if it wasn't drawn in the loop
    // This ensures the right edge of the last content cell is always visible
    if (!drawnRightBoundary && contentClamp.right !== Infinity && bounds.right >= contentClamp.right) {
      this.moveTo(contentClamp.right, top);
      this.lineTo(contentClamp.right, bottom);
    }
  }

  // @returns the vertical range of [rowStart, rowEnd]
  private drawHorizontalLines(
    bounds: Rectangle,
    mergedRects: Rect[],
    startCol: number,
    endCol: number
  ): [number, number] {
    const sheet = sheets.sheet;
    const offsets = sheet.offsets;
    const rowPlacement = offsets.getYPlacement(bounds.top);
    const index = rowPlacement.index;
    const position = rowPlacement.position;
    const gridOverflowLines = sheets.sheet.gridOverflowLines;
    const contentClamp = sheet.getContentClamp();

    const left = bounds.left <= contentClamp.left ? contentClamp.left : bounds.left;
    const right = bounds.right >= contentClamp.right ? contentClamp.right : bounds.right;

    // draw 0-line if it's visible (since it's not part of the sheet anymore)
    if (bounds.top <= contentClamp.top) {
      this.moveTo(left, 0);
      this.lineTo(right, 0);
    }

    // Track if we've drawn the bottom content boundary line
    let drawnBottomBoundary = false;

    let row = index;
    const offset = bounds.top - position;
    let size = 0;

    for (let y = bounds.top; y <= bounds.bottom + size - 1; y += size) {
      // don't draw grid lines when hidden or beyond content clamp
      // Use the actual drawing position (y - offset) for content clamp comparison
      const drawY = y - offset;
      if (size !== 0 && drawY >= contentClamp.top && drawY <= contentClamp.bottom) {
        // Check if this is the bottom content boundary
        if (drawY === contentClamp.bottom) {
          drawnBottomBoundary = true;
        }
        // Get overflow lines (excludes overflow areas)
        const overflowLines = gridOverflowLines.getRowHorizontalRange(row, [startCol, endCol]);

        // Precalculate excluded column ranges due to merged cells
        const mergedExcludedCols = getMergedCellExcludedColumnsForRow(row, [startCol, endCol], mergedRects);

        // Compute ranges to draw, combining overflow and merged cell exclusions
        // Follows the same pattern as GridOverflowLines
        const linesToDraw = getRowHorizontalRangesToDraw(row, [startCol, endCol], overflowLines, mergedExcludedCols);

        // Draw the calculated line segments
        if (linesToDraw && linesToDraw.length > 0) {
          for (const [x0, x1] of linesToDraw) {
            const start = offsets.getColumnPlacement(x0).position;
            const end = Math.min(offsets.getColumnPlacement(x1 + 1).position, right);
            this.moveTo(start, y - offset);
            this.lineTo(end, y - offset);
          }
        } else if (linesToDraw === undefined) {
          // No exclusions, draw the full line
          this.moveTo(left, y - offset);
          this.lineTo(right, y - offset);
        }
        // If linesToDraw is empty array, don't draw anything

        this.gridLinesY.push({ row, x: bounds.left, y: y - offset, w: right - left, h: 1 });
      }
      size = offsets.getRowHeight(row);
      row++;
    }

    // Draw the bottom content boundary line if it wasn't drawn in the loop
    // This ensures the bottom edge of the last content cell is always visible
    if (!drawnBottomBoundary && contentClamp.bottom !== Infinity && bounds.bottom >= contentClamp.bottom) {
      this.moveTo(left, contentClamp.bottom);
      this.lineTo(right, contentClamp.bottom);
    }

    return [index, row - 1];
  }
}
